import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, map, of, switchMap, take, merge } from 'rxjs';

import { V1_AuthService } from '@app/api/v1/auth/v1-auth.service';
import { PermissionService } from '@app/auth/services/permission.service';
import { V1AuthUser } from '@app/models/api/v1/auth/auth.model';
import { navigationActions } from '@app/store/actions/navigation.actions';
import { holdingActions, holdingCompaniesActions } from '../actions/holding.actions';
import { loginActions } from '../actions/login.actions';
import { userInfoActions } from '../actions/user-info.actions';

@Injectable()
export class HoldingEffects {
  public readonly changeCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(holdingActions.start),
      exhaustMap(({ params }) =>
        this.v1_AuthService.holdingLogin(params).pipe(
          map(response => holdingActions.success({ response })),
          catchError(() => of(holdingActions.error())),
        ),
      ),
    ),
  );

  public readonly getHoldingCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(holdingCompaniesActions.start),
      exhaustMap(() =>
        this.v1_AuthService.getHoldingCompanies().pipe(
          map(response => holdingCompaniesActions.success({ response: response.data })),
          catchError(() => of(holdingCompaniesActions.error())),
        ),
      ),
    ),
  );

  public readonly handleLoginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(holdingActions.success),
      map(({ response: { data } }) => loginActions.success({ response: data, skipNavigation: true })),
    ),
  );

  public readonly reloadCurrentRouteOnSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(holdingActions.success),
      map(() => navigationActions.reloadCurrentRoute()),
    ),
  );

  public readonly fetchHoldingCompaniesOnUserLoad$ = createEffect(() => {
    const userFromUserInfo$ = this.actions$.pipe(
      ofType(userInfoActions.success),
      map(a => a.response),
    );
    const userFromLogin$ = this.actions$.pipe(
      ofType(loginActions.success),
      map(a => a.response.user),
    );

    return merge(userFromUserInfo$, userFromLogin$).pipe(
      switchMap((user: V1AuthUser) =>
        this.permissionService.canAccessSettingsModule().pipe(
          take(1),
          map(canAccess => ({ user, canAccess })),
        ),
      ),
      switchMap(({ user, canAccess }) => {
        if (canAccess && user?.company?.is_within_holding) {
          return of(holdingCompaniesActions.start());
        }
        return of(holdingCompaniesActions.clear());
      }),
    );
  });

  constructor(
    private actions$: Actions,
    private v1_AuthService: V1_AuthService,
    private store: Store,
    private permissionService: PermissionService,
  ) {}
}
